import { useMutation, UseMutationOptions } from 'react-query';
import { useDispatch } from 'react-redux';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { modalOpen } from '@app/store/modal/actions';
import { getTemplateById } from '@app/store/transport-layer/template';
import { documentUpload } from '@app/store/transport-layer/upload/document/upload';
import { Dispatch } from '@app/store/types';

import { checkContractLinkLimits, contractLinkCreateLoadPdfRequest } from '../../actions';
import { MaxActiveLinksError, PARTICIPANTS_COUNT_ERROR } from '../../convertToContractLinkErrors';

type Options = UseMutationOptions<void, Error | MaxActiveLinksError, string>;

export default function useStartContractLinkByTemplate(options?: Options) {
  const dispatch = useDispatch<Dispatch>();
  const workspaceId = useWorkspaceId();

  return useMutation<void, Error | MaxActiveLinksError, string>(
    async (templateId: string) => {
      const templateData = await getTemplateById(workspaceId, templateId);

      if (templateData.participants.filter((p) => p.type === 'SIGNER').length !== 1) {
        throw new Error(PARTICIPANTS_COUNT_ERROR);
      }

      const checkLimitResultAction = await dispatch(checkContractLinkLimits());

      if (checkContractLinkLimits.fulfilled.match(checkLimitResultAction)) {
        /**
         * TODO: @Juyeong-Byeon 이슈 논의 후 처리
         * 비즈니스 규칙에 따른 분기를 try catch로 처리하는 부분에 대하여
         * https://github.com/modusign/modusign-client/issues/9103
         *
         */
        const { payload } = checkLimitResultAction;
        if (payload.isLimitExceeded) {
          throw new MaxActiveLinksError(
            payload.concurrentActiveLinksLimit,
            payload.activeLinkCount
          );
        }

        const fileData = await documentUpload({
          name: `${templateData.title}.pdf`,
          url: templateData.file?.url ?? '',
        });

        const { fileId, title, url } = fileData;
        dispatch(contractLinkCreateLoadPdfRequest({ fileId, title, url }));
        dispatch(
          modalOpen('CONTRACT_LINK_START_CONFIRM_MODAL', {
            source: {
              type: 'template',
              id: templateData.id,
              labelIds: templateData.labelIds,
            },
          })
        );
      }
      if (checkContractLinkLimits.rejected.match(checkLimitResultAction)) {
        throw new Error(checkLimitResultAction.error.message);
      }
    },
    {
      ...options,
    }
  );
}
